import { api, http } from '../client';
import type { PageParams } from '@/types/api';
import type { IsoDate, UUID } from '@/types/domain';
import type {
  DriverInput,
  DriverReport,
  DriverRole,
  DriverStatus,
  EnrollmentStopChangeInput,
  IncidentInput,
  IncidentReport,
  IncidentSearchParams,
  MaintenanceDue,
  MaintenanceInput,
  MaintenanceRecord,
  MaintenanceReport,
  MaintenanceSchedule,
  MaintenanceScheduleInput,
  MaintenanceSearchParams,
  MaintenanceStatus,
  RouteDirection,
  RouteInput,
  RouteReport,
  RouteStop,
  RouteStopInput,
  StudentTransportReport,
  TransportAttendanceReport,
  TransportDashboard,
  TransportDocument,
  TransportDocumentMetadataInput,
  TransportDriver,
  TransportEnrollment,
  TransportEnrollmentInput,
  TransportEnrollmentStatus,
  TransportEvent,
  TransportIncident,
  TransportRoute,
  TransportSettings,
  TransportSettingsInput,
  TransportStatus,
  TransportTrip,
  TripAttendanceInput,
  TripGenerationResult,
  TripInput,
  TripReport,
  TripRoster,
  TripSearchParams,
  Vehicle,
  VehicleInput,
  VehicleLocation,
  VehicleLocationInput,
  VehicleReport,
  VehicleStatus,
} from '@/types/transport';

/*
 * The transport module's endpoints.
 *
 * Two things run through the whole file.
 *
 * Every listing is paged and every search is a server-side query — the fleet,
 * the riders and the trip history are never shipped to the browser whole
 * (§44/§46).
 *
 * Nothing here calculates. Capacity arithmetic, driver eligibility, document
 * compliance, billing state and tracking freshness all arrive computed,
 * because §38 says a critical rule must not live only in React. Where a screen
 * shows a reason ("cannot be assigned because…"), that string came from the
 * server.
 */

// ----------------------------------------------------------------- vehicles

export const vehiclesApi = {
  /** Search matches plate, fleet number, make and model — on the server. */
  list: (params: PageParams & { status?: VehicleStatus; campusId?: UUID }) =>
    api.getPage<Vehicle>('/transport/vehicles', { params }),

  get: (id: UUID) => api.get<Vehicle>(`/transport/vehicles/${id}`),
  create: (payload: VehicleInput) => api.post<Vehicle>('/transport/vehicles', payload),
  update: (id: UUID, payload: VehicleInput) =>
    api.put<Vehicle>(`/transport/vehicles/${id}`, payload),

  /** Retiring is final and needs a reason; the vehicle's history stays. */
  setStatus: (id: UUID, status: VehicleStatus, reason?: string) =>
    api.patch<Vehicle>(`/transport/vehicles/${id}/status`, undefined, {
      params: { status, reason },
    }),

  documents: (id: UUID) => api.get<TransportDocument[]>(`/transport/vehicles/${id}/documents`),
  uploadDocument: (id: UUID, file: File, metadata: TransportDocumentMetadataInput) =>
    uploadDocument(`/transport/vehicles/${id}/documents`, file, metadata),
  downloadDocument: (id: UUID, documentId: UUID, fileName: string) =>
    downloadFile(`/transport/vehicles/${id}/documents/${documentId}/download`, {}, fileName),
  deleteDocument: (id: UUID, documentId: UUID) =>
    api.delete(`/transport/vehicles/${id}/documents/${documentId}`),
};

// ------------------------------------------------------------------ drivers

export const transportDriversApi = {
  list: (params: PageParams & { status?: DriverStatus; role?: DriverRole }) =>
    api.getPage<TransportDriver>('/transport/drivers', { params }),

  /** One driver, with their eligibility spelled out in the server's words. */
  get: (id: UUID) => api.get<TransportDriver>(`/transport/drivers/${id}`),

  /** Points at an existing employee — the person is never duplicated. */
  create: (payload: DriverInput) => api.post<TransportDriver>('/transport/drivers', payload),
  update: (id: UUID, payload: DriverInput) =>
    api.put<TransportDriver>(`/transport/drivers/${id}`, payload),

  /** Suspending or benching needs a reason. Separate from HR's employment status. */
  setStatus: (id: UUID, status: DriverStatus, reason?: string) =>
    api.patch<TransportDriver>(`/transport/drivers/${id}/status`, undefined, {
      params: { status, reason },
    }),

  documents: (id: UUID) => api.get<TransportDocument[]>(`/transport/drivers/${id}/documents`),
  uploadDocument: (id: UUID, file: File, metadata: TransportDocumentMetadataInput) =>
    uploadDocument(`/transport/drivers/${id}/documents`, file, metadata),
  downloadDocument: (id: UUID, documentId: UUID, fileName: string) =>
    downloadFile(`/transport/drivers/${id}/documents/${documentId}/download`, {}, fileName),
  deleteDocument: (id: UUID, documentId: UUID) =>
    api.delete(`/transport/drivers/${id}/documents/${documentId}`),
};

// ------------------------------------------------------------------- routes

export const transportRoutesApi = {
  /** The route network, with per-route capacity and staffing. */
  list: (
    params: PageParams & {
      status?: TransportStatus;
      direction?: RouteDirection;
      campusId?: UUID;
    },
  ) => api.getPage<TransportRoute>('/transport/routes', { params }),

  /** One route with its ordered stops. */
  get: (id: UUID) => api.get<TransportRoute>(`/transport/routes/${id}`),
  create: (payload: RouteInput) => api.post<TransportRoute>('/transport/routes', payload),
  update: (id: UUID, payload: RouteInput) =>
    api.put<TransportRoute>(`/transport/routes/${id}`, payload),

  /** Refused while students are enrolled on it. */
  setStatus: (id: UUID, status: TransportStatus, reason?: string) =>
    api.patch<TransportRoute>(`/transport/routes/${id}/status`, undefined, {
      params: { status, reason },
    }),

  // ------------------------------------------------------------------ stops

  stops: (id: UUID) => api.get<RouteStop[]>(`/transport/routes/${id}/stops`),
  addStop: (id: UUID, payload: RouteStopInput) =>
    api.post<RouteStop>(`/transport/routes/${id}/stops`, payload),
  updateStop: (id: UUID, stopId: UUID, payload: RouteStopInput) =>
    api.put<RouteStop>(`/transport/routes/${id}/stops/${stopId}`, payload),

  /** Refused while students board or alight there. The rest renumber densely. */
  removeStop: (id: UUID, stopId: UUID) =>
    api.delete(`/transport/routes/${id}/stops/${stopId}`),

  /** The body is every stop id in the new order — a partial list is refused. */
  reorderStops: (id: UUID, orderedStopIds: UUID[]) =>
    api.put<RouteStop[]>(`/transport/routes/${id}/stops/order`, orderedStopIds),

  // ------------------------------------------------------------ assignments

  /**
   * Put a vehicle on the route. Closes the previous assignment rather than
   * overwriting it; refusals (benched vehicle, double booking, too few seats)
   * come back as server errors with the reason.
   */
  assignVehicle: (id: UUID, vehicleId: UUID, from?: IsoDate) =>
    api.post<TransportRoute>(`/transport/routes/${id}/vehicle`, undefined, {
      params: { vehicleId, from },
    }),
  unassignVehicle: (id: UUID, reason?: string) =>
    api.delete<TransportRoute>(`/transport/routes/${id}/vehicle`, { params: { reason } }),

  /** Eligibility is checked first; a refusal names the exact blockers. */
  assignDriver: (id: UUID, driverId: UUID, from?: IsoDate) =>
    api.post<TransportRoute>(`/transport/routes/${id}/driver`, undefined, {
      params: { driverId, from },
    }),
  unassignDriver: (id: UUID, role: DriverRole = 'DRIVER', reason?: string) =>
    api.delete<TransportRoute>(`/transport/routes/${id}/driver`, { params: { role, reason } }),
};

// -------------------------------------------------------------- enrollments

export const transportEnrollmentsApi = {
  list: (
    params: PageParams & {
      status?: TransportEnrollmentStatus;
      academicYearId?: UUID;
      routeId?: UUID;
    },
  ) => api.getPage<TransportEnrollment>('/transport/enrollments', { params }),

  get: (id: UUID) => api.get<TransportEnrollment>(`/transport/enrollments/${id}`),

  /** A student's transport history, newest first. */
  forStudent: (studentId: UUID) =>
    api.get<TransportEnrollment[]>(`/transport/enrollments/students/${studentId}`),

  /** Starts PENDING — a seat is held but nothing is billed until activation. */
  enrol: (payload: TransportEnrollmentInput) =>
    api.post<TransportEnrollment>('/transport/enrollments', payload),

  /** The child rides from here, and the finance assignment starts with them. */
  activate: (id: UUID) => api.post<TransportEnrollment>(`/transport/enrollments/${id}/activate`),

  /** The seat stays held and billing pauses with the service. */
  suspend: (id: UUID, reason: string) =>
    api.post<TransportEnrollment>(`/transport/enrollments/${id}/suspend`, undefined, {
      params: { reason },
    }),

  /** Ends the fee assignment; months already invoiced stand. The seat frees. */
  cancel: (id: UUID, reason: string) =>
    api.post<TransportEnrollment>(`/transport/enrollments/${id}/cancel`, undefined, {
      params: { reason },
    }),

  complete: (id: UUID) => api.post<TransportEnrollment>(`/transport/enrollments/${id}/complete`),

  /** The routine mid-year change of routes, stops or fee. */
  changeStops: (id: UUID, payload: EnrollmentStopChangeInput) =>
    api.put<TransportEnrollment>(`/transport/enrollments/${id}/stops`, payload),
};

// -------------------------------------------------------------------- trips

export const tripsApi = {
  list: (params: PageParams & TripSearchParams) =>
    api.getPage<TransportTrip>('/transport/trips', { params }),

  /** The day's board — every trip in start order. Defaults to today. */
  board: (date?: IsoDate) => api.get<TransportTrip[]>('/transport/trips/board', { params: { date } }),

  get: (id: UUID) => api.get<TransportTrip>(`/transport/trips/${id}`),

  /** Vehicle and driver default to the route's open assignments. */
  schedule: (payload: TripInput) => api.post<TransportTrip>('/transport/trips', payload),

  /**
   * Create the day's trips for every staffed route. A button, not a
   * scheduler — idempotent, and unstaffable routes come back with reasons.
   */
  generate: (date?: IsoDate) =>
    api.post<TripGenerationResult>('/transport/trips/generate', undefined, { params: { date } }),

  start: (id: UUID) => api.post<TransportTrip>(`/transport/trips/${id}/start`),
  depart: (id: UUID) => api.post<TransportTrip>(`/transport/trips/${id}/depart`),
  complete: (id: UUID, notes?: string) =>
    api.post<TransportTrip>(`/transport/trips/${id}/complete`, undefined, { params: { notes } }),

  /** Records the delay and — when delay notices are on — a guardian event per rider. */
  delay: (id: UUID, minutes: number, reason?: string) =>
    api.post<TransportTrip>(`/transport/trips/${id}/delay`, undefined, {
      params: { minutes, reason },
    }),

  cancel: (id: UUID, reason: string) =>
    api.post<TransportTrip>(`/transport/trips/${id}/cancel`, undefined, { params: { reason } }),

  /** The Tuesday stand-in: lives on the trip, assignment history untouched. */
  substitute: (
    id: UUID,
    swap: { vehicleId?: UUID; driverId?: UUID; attendantId?: UUID },
  ) => api.post<TransportTrip>(`/transport/trips/${id}/substitute`, undefined, { params: swap }),

  // ------------------------------------------------------------- attendance

  /** The trip's manifest. Unmarked rows are absent — never defaulted to boarded. */
  roster: (id: UUID) => api.get<TripRoster>(`/transport/trips/${id}/roster`),

  /** Batch or single tap — the payload is a list either way. */
  mark: (id: UUID, payload: TripAttendanceInput) =>
    api.post<TripRoster>(`/transport/trips/${id}/attendance`, payload),
};

// ---------------------------------------------------------------- incidents

export const incidentsApi = {
  list: (params: PageParams & IncidentSearchParams) =>
    api.getPage<TransportIncident>('/transport/incidents', { params }),

  get: (id: UUID) => api.get<TransportIncident>(`/transport/incidents/${id}`),

  /** CRITICAL severity and MISSING_STUDENT escalate themselves. */
  report: (payload: IncidentInput) =>
    api.post<TransportIncident>('/transport/incidents', payload),

  review: (id: UUID) => api.post<TransportIncident>(`/transport/incidents/${id}/review`),

  /** Notifies the involved students' guardians — kind and severity only. */
  escalate: (id: UUID, notes?: string) =>
    api.post<TransportIncident>(`/transport/incidents/${id}/escalate`, undefined, {
      params: { notes },
    }),

  recordAction: (id: UUID, actionTaken: string) =>
    api.post<TransportIncident>(`/transport/incidents/${id}/action`, undefined, {
      params: { actionTaken },
    }),

  resolve: (id: UUID, resolution: string) =>
    api.post<TransportIncident>(`/transport/incidents/${id}/resolve`, undefined, {
      params: { resolution },
    }),

  close: (id: UUID) => api.post<TransportIncident>(`/transport/incidents/${id}/close`),
};

// -------------------------------------------------------------- maintenance

export const maintenanceApi = {
  list: (params: PageParams & MaintenanceSearchParams) =>
    api.getPage<MaintenanceRecord>('/transport/maintenance', { params }),

  /** What is due or overdue across the fleet — worst first, server-computed. */
  due: () => api.get<MaintenanceDue[]>('/transport/maintenance/due'),

  /** Costs are reporting figures — no payment or ledger entry is created. */
  create: (payload: MaintenanceInput) =>
    api.post<MaintenanceRecord>('/transport/maintenance', payload),
  update: (id: UUID, payload: MaintenanceInput) =>
    api.put<MaintenanceRecord>(`/transport/maintenance/${id}`, payload),

  /** IN_PROGRESS benches the vehicle; COMPLETED releases it and rolls things forward. */
  setStatus: (id: UUID, status: MaintenanceStatus) =>
    api.patch<MaintenanceRecord>(`/transport/maintenance/${id}/status`, undefined, {
      params: { status },
    }),

  // -------------------------------------------------------------- schedules

  schedules: (vehicleId: UUID) =>
    api.get<MaintenanceSchedule[]>(`/transport/maintenance/vehicles/${vehicleId}/schedules`),

  /** One schedule per (vehicle, type); saving again replaces the intervals. */
  saveSchedule: (vehicleId: UUID, payload: MaintenanceScheduleInput) =>
    api.put<MaintenanceSchedule>(
      `/transport/maintenance/vehicles/${vehicleId}/schedules`,
      payload,
    ),

  deleteSchedule: (vehicleId: UUID, scheduleId: UUID) =>
    api.delete(`/transport/maintenance/vehicles/${vehicleId}/schedules/${scheduleId}`),
};

// ----------------------------------------------------------------- tracking

export const vehicleTrackingApi = {
  /** Every active vehicle: LIVE, DELAYED, STALE or UNAVAILABLE — silence for none. */
  fleet: () => api.get<VehicleLocation[]>('/transport/tracking'),

  lastKnown: (vehicleId: UUID) =>
    api.get<VehicleLocation>(`/transport/tracking/vehicles/${vehicleId}`),

  /** The GPS integration seam: driver app, vendor webhook or a manual pin. */
  record: (vehicleId: UUID, payload: VehicleLocationInput) =>
    api.post<VehicleLocation>(`/transport/tracking/vehicles/${vehicleId}`, payload),
};

// ----------------------------------------------------------------- settings

export const transportSettingsApi = {
  get: () => api.get<TransportSettings>('/transport/settings'),

  /** Partial update: omitted fields keep their value. */
  update: (payload: TransportSettingsInput) =>
    api.put<TransportSettings>('/transport/settings', payload),
};

// ------------------------------------------------------ dashboard and events

export const transportDashboardApi = {
  /** KPIs and the alert list. Every figure is a fact; nothing is estimated. */
  dashboard: () => api.get<TransportDashboard>('/transport/dashboard'),

  /** The transport outbox. pending=true shows only undelivered rows. */
  events: (params: PageParams & { pending?: boolean }) =>
    api.getPage<TransportEvent>('/transport/events', { params }),
};

// -------------------------------------------------------------- self-service

/**
 * A driver's own world, resolved from their account — no identifier to tamper
 * with. Everything under /transport/me is scoped server-side.
 */
export const driverSelfApi = {
  /** Defaults to today plus the coming week. */
  myTrips: (from?: IsoDate, to?: IsoDate) =>
    api.get<TransportTrip[]>('/transport/me/trips', { params: { from, to } }),

  roster: (tripId: UUID) => api.get<TripRoster>(`/transport/me/trips/${tripId}/roster`),

  mark: (tripId: UUID, payload: TripAttendanceInput) =>
    api.post<TripRoster>(`/transport/me/trips/${tripId}/attendance`, payload),

  start: (tripId: UUID) => api.post<TransportTrip>(`/transport/me/trips/${tripId}/start`),
  depart: (tripId: UUID) => api.post<TransportTrip>(`/transport/me/trips/${tripId}/depart`),
  complete: (tripId: UUID, notes?: string) =>
    api.post<TransportTrip>(`/transport/me/trips/${tripId}/complete`, undefined, {
      params: { notes },
    }),
  delay: (tripId: UUID, minutes: number, reason?: string) =>
    api.post<TransportTrip>(`/transport/me/trips/${tripId}/delay`, undefined, {
      params: { minutes, reason },
    }),
};

// ------------------------------------------------------------------ reports

/**
 * §34's reports. Dated ones default (on the server) to the last thirty days.
 * CSVs come from the same figures as the JSON.
 */
export const transportReportsApi = {
  students: (routeId?: UUID, status?: TransportEnrollmentStatus) =>
    api.get<StudentTransportReport>('/transport/reports/students', {
      params: { routeId, status },
    }),
  studentsCsv: (routeId?: UUID, status?: TransportEnrollmentStatus) =>
    downloadFile('/transport/reports/students.csv', { routeId, status }, 'transport-students.csv'),

  routes: () => api.get<RouteReport>('/transport/reports/routes'),
  routesCsv: () => downloadFile('/transport/reports/routes.csv', {}, 'transport-routes.csv'),

  vehicles: (from?: IsoDate, to?: IsoDate) =>
    api.get<VehicleReport>('/transport/reports/vehicles', { params: { from, to } }),
  vehiclesCsv: (from?: IsoDate, to?: IsoDate) =>
    downloadFile('/transport/reports/vehicles.csv', { from, to }, 'transport-vehicles.csv'),

  drivers: () => api.get<DriverReport>('/transport/reports/drivers'),
  driversCsv: () => downloadFile('/transport/reports/drivers.csv', {}, 'transport-drivers.csv'),

  trips: (from?: IsoDate, to?: IsoDate) =>
    api.get<TripReport>('/transport/reports/trips', { params: { from, to } }),
  tripsCsv: (from?: IsoDate, to?: IsoDate) =>
    downloadFile('/transport/reports/trips.csv', { from, to }, 'transport-trips.csv'),

  attendance: (from?: IsoDate, to?: IsoDate) =>
    api.get<TransportAttendanceReport>('/transport/reports/attendance', { params: { from, to } }),
  attendanceCsv: (from?: IsoDate, to?: IsoDate) =>
    downloadFile('/transport/reports/attendance.csv', { from, to }, 'transport-attendance.csv'),

  maintenance: (from?: IsoDate, to?: IsoDate) =>
    api.get<MaintenanceReport>('/transport/reports/maintenance', { params: { from, to } }),
  maintenanceCsv: (from?: IsoDate, to?: IsoDate) =>
    downloadFile('/transport/reports/maintenance.csv', { from, to }, 'transport-maintenance.csv'),

  incidents: (from?: IsoDate, to?: IsoDate) =>
    api.get<IncidentReport>('/transport/reports/incidents', { params: { from, to } }),
  incidentsCsv: (from?: IsoDate, to?: IsoDate) =>
    downloadFile('/transport/reports/incidents.csv', { from, to }, 'transport-incidents.csv'),
};

// ----------------------------------------------------------------- internal

/**
 * Upload a file with its metadata as a JSON part.
 *
 * The content type is set explicitly on that part — without it the browser
 * sends `text/plain` for a Blob and Spring refuses to bind it. The outer
 * Content-Type is left undefined so the browser sets multipart/form-data with
 * its boundary.
 */
async function uploadDocument(
  path: string,
  file: File,
  metadata: TransportDocumentMetadataInput,
): Promise<TransportDocument> {
  const form = new FormData();
  form.append('file', file);
  form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));

  const response = await http.post<{ data: TransportDocument }>(path, form, {
    headers: { 'Content-Type': undefined },
  });
  return response.data.data;
}

/**
 * Fetch a file and hand the browser a save prompt.
 *
 * Fetched as a blob rather than opened as a link: the request needs an
 * Authorization header, and a plain anchor cannot carry one.
 */
async function downloadFile(path: string, params: Record<string, unknown>, filename: string) {
  const response = await http.get(path, { params, responseType: 'blob' });

  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
